export enum StaticMessage {
  // titles
  ValidationErrorTitle = 1,
  SaveErrorTitle,
  SyncErrorTitle,
  PartialSyncErrorTitle,
  UploadErrorTitle,
  DeleteSelectedItemTitle,
  WarningTitle,
  AutoFillTitle,
  ErrorTitle,
  UnsavedChangesTitle,

  // texts
  DeleteSelectedItemText,
  HandledInCoreText,
  NoGridRecordsText,
  AutoFillText,
  RevisionText,
  ArrayMoreText,
  InactiveText,
  UnsavedChangesText,
  CopyText,
  CopyOperationText,
  InactivateDependentColumnsText,
  InactivateDependentItemsText,
  InactivateColumnGroupDependentColumnsText,
  ActivateColumnGroupAffectedColumnsText,

  // buttons
  OkButton,
  DeleteButton,
  CancelButton,
  DoneButton,
  CloseButton,
  ClearButton,
  ContinueButton,
  DownloadButton,
  SaveButton,
  CopyButton,
  ImportButton,
  InsertButton,

  // validation errors
  FieldMandatoryError,
  FieldDuplicateError,
  FieldMaxLengthError,
  FieldNameCannotBeAll,
  FieldNameContainsQuotationMark,
  FieldNameIsReservedExpression,
  FieldNameContainsInvalidCharacters,
  FieldNameMustContainLetter,
  EntitiesCountExceeded,
  InlineCustomDataExceeded,
  TabNameDuplicate,

  // formula validation errors
  MissingOperator,
  InvalidOperatorAfterOpeningParenthesis,
  InvalidFormulaValues,
  InvalidParanthesesPlacement,
  CircularReferencesFound,
  FormulaRequiresMultipleVariables,
  FormulaRequiresAtLeastOneOperator,
  ConsecutiveOperators,
  InvalidStartingOperator,
  TrailingOperator,
  DivisionByZero,

  // error messages
  SaveErrorTryAgain,
  SyncErrorTryAgain,
  PartialSyncError,
  LockedDprError,

  // tooltips
  CannotDisableItemUsedInSubprojectsTooltip,
  CannotDeleteRowTooltip,
  CannotActivateFormulaColumnTooltip,
  CannotActivateCarryOverColumnTooltip,
  DeleteRowTooltip,
  ViewModeTooltip,
  CopyFromPreviousDprTooltip,
  UnavailableInOfflineTooltip,
  NoEditRightsTooltip,
  CanNotCopyFirstDprTooltip,
  CopyFromPrevDprNotEnabledTooltip,
  NoActiveSubtabsTooltip,
  CanNotCopyIfDataTooltip,
  CanNotCopyIfLockedDpr,
  CanNotCopyWhileDataInSectionTooltip,
  CanNotDownloadDataNoDownloadRightsTooltip,
  CanNotDownloadDataNoDprsTooltip,
  ProjectIsReleasedTooltip,
  ProjectBasicPersonnelVersionTooltip,
  CanNotDeactivateMandatoryTabsTooltip,
  CanNotDeactivateTooltip,
  AddShortcutTooltip,

  // grid columns
  PriorityColumn,
  CommentColumn,
  ActiveColumn,
  MandatoryColumn,
  PreviousColumn,
  TodayColumn,
  TotalColumn,
  ActivityGroupColumn,
  ActivityColumn,
  LocationColumn,
  StartColumn,
  FinishColumn,
  DurationColumn,
  DataTypeColumn,

  // labels
  RegistrationsLabel,
  AttachmentsLabel,
  CommentLabel,
  CommentPluralLabel,
  CustomDataLabel,
  UnassignedLabel,
  EntryLabel,
  EntriesLabel,
  AllLabel,
  SearchLabel,
  NoSearchResultsLabel,

  // select options
  NoneOptionName,
  AllActivityStreamsOption,

  // entity labels
  DefaultEntityNameLabel,
  DefaultEntityPluralNameLabel,
  DefaultEntityAddNameLabel,

  // warning data types
  WarningLocationCategory,
  WarningActivityGroup,
  WarningCustomData,
  WarningActivityStream,
  WarningActivity,
  WarningLocation,
  WarningUndeclaredActivity,

  // limits
  MaxLimitMessage,
}

export const dynamicTextPlaceholder = '(---)';

export const StaticMessageText = new Map<number, string>([
  // titles
  [
    StaticMessage.ValidationErrorTitle,
    $localize`:@@shared.staticMessage.titleValidationError:Validation error`,
  ],
  [
    StaticMessage.SaveErrorTitle,
    $localize`:@@shared.staticMessage.titleSaveError:Save error`,
  ],
  [
    StaticMessage.SyncErrorTitle,
    $localize`:@@shared.staticMessage.titleSyncErrorTitle:Sync error`,
  ],
  [
    StaticMessage.PartialSyncErrorTitle,
    $localize`:@@shared.staticMessage.titlePartialSyncErrorTitle:Partial sync error`,
  ],
  [
    StaticMessage.UploadErrorTitle,
    $localize`:@@shared.staticMessage.titleUploadError:Upload Error`,
  ],
  [
    StaticMessage.DeleteSelectedItemTitle,
    $localize`:@@shared.staticMessage.titleDeleteSelectedItem:Delete selected item?`,
  ],
  [
    StaticMessage.WarningTitle,
    $localize`:@@shared.staticMessage.titleWarning:Warning`,
  ],
  [
    StaticMessage.AutoFillTitle,
    $localize`:@@shared.staticMessage.titleAutoFillTitle:Auto-Fill (---)`,
  ],
  [
    StaticMessage.ErrorTitle,
    $localize`:@@shared.staticMessage.titleError:Error`,
  ],
  [
    StaticMessage.UnsavedChangesTitle,
    $localize`:@@shared.staticMessage.titleUnsavedChanges:Unsaved data`,
  ],

  // texts
  [
    StaticMessage.DeleteSelectedItemText,
    $localize`:@@shared.staticMessage.txtDeleteSelectedItem:Are you sure you want to delete the selected item?`,
  ],
  [
    StaticMessage.HandledInCoreText,
    $localize`:@@shared.staticMessage.txtHandledInCoreAsOfSeptember:As of September 2020 (---) capabilities are handled in Core`,
  ],
  [
    StaticMessage.NoGridRecordsText,
    $localize`:@@shared.staticMessage.txtNoGridRecords:No records to display`,
  ],
  [
    StaticMessage.AutoFillText,
    $localize`:@@shared.staticMessage.txtAutoFill:Empty (---) registrations will be auto-filled with 0.`,
  ],
  [
    StaticMessage.RevisionText,
    $localize`:@@shared.staticMessage.txtRevision:Revision`,
  ],
  [
    StaticMessage.ArrayMoreText,
    $localize`:@@shared.staticMessage.txtArrayMore:+(---) more`,
  ],
  [
    StaticMessage.InactiveText,
    $localize`:@@shared.staticMessage.txtInactive:(Inactive)`,
  ],
  [
    StaticMessage.UnsavedChangesText,
    $localize`:@@shared.staticMessage.txtUnsavedChanges:By performing the (---) operation all unsaved changes will be lost. Press Cancel to stay on the page and save changes, or Continue to go forward (---).`,
  ],
  [StaticMessage.CopyText, $localize`:@@shared.staticMessage.txtCopy:copy`],
  [
    StaticMessage.CopyOperationText,
    $localize`:@@shared.staticMessage.txtCopyOperation:with the copy operation`,
  ],
  [
    StaticMessage.InactivateDependentColumnsText,
    $localize`:@@shared.staticMessage.txtInactivateDependentColumns:By inactivating this column, all the columns that use it in a formula or carry-over will also be inactivated.<br>Affected columns: (---).`,
  ],
  [
    StaticMessage.InactivateDependentItemsText,
    $localize`:@@shared.staticMessage.txtInactivateDependentItems:By inactivating this item, all the items that use it in a formula or carry-over will also be inactivated.<br>Affected items: (---).`,
  ],
  [
    StaticMessage.InactivateColumnGroupDependentColumnsText,
    $localize`:@@shared.staticMessage.txtInactivateColumnGroupDependentColumns:By inactivating this column group, all the columns that use any of its columns in a formula or carry-over will also be inactivated.<br>Affected columns: (---).`,
  ],
  [
    StaticMessage.ActivateColumnGroupAffectedColumnsText,
    $localize`:@@shared.staticMessage.txtActivateColumnGroupAffectedColumns:Columns (---) use inactive columns in their configurations. Activating (---) will also inactivate these columns and any columns that depend on them.`,
  ],

  // buttons
  [StaticMessage.OkButton, $localize`:@@shared.staticMessage.btnOk:Ok`],
  [
    StaticMessage.DeleteButton,
    $localize`:@@shared.staticMessage.btnDelete:Delete`,
  ],
  [
    StaticMessage.CancelButton,
    $localize`:@@shared.staticMessage.btnCancel:Cancel`,
  ],
  [StaticMessage.DoneButton, $localize`:@@shared.staticMessage.btnDone:Done`],
  [
    StaticMessage.CloseButton,
    $localize`:@@shared.staticMessage.btnClose:Close`,
  ],
  [
    StaticMessage.ClearButton,
    $localize`:@@shared.staticMessage.btnClear:Clear`,
  ],
  [
    StaticMessage.ContinueButton,
    $localize`:@@shared.staticMessage.btnContinue:Continue`,
  ],
  [
    StaticMessage.DownloadButton,
    $localize`:@@shared.staticMessage.btnDownload:Download`,
  ],
  [StaticMessage.SaveButton, $localize`:@@shared.staticMessage.btnSave:Save`],
  [StaticMessage.CopyButton, $localize`:@@shared.staticMessage.btnCopy:Copy`],
  [
    StaticMessage.ImportButton,
    $localize`:@@shared.staticMessage.btnImport:Import Data`,
  ],
  [
    StaticMessage.InsertButton,
    $localize`:@@shared.staticMessage.btnInsert:Import`,
  ],

  // validation errors
  [
    StaticMessage.FieldMandatoryError,
    $localize`:@@shared.staticMessage.errFieldMandatory:- (---) field is mandatory.`,
  ],
  [
    StaticMessage.FieldDuplicateError,
    $localize`:@@shared.staticMessage.errFieldDuplicate:is duplicated. Please define each (---) only once.`,
  ],
  [
    StaticMessage.FieldMaxLengthError,
    $localize`:@@shared.staticMessage.errFieldMaxLength:(---)Maximum length of (---) characters exceeded(---).`,
  ],
  [
    StaticMessage.FieldNameCannotBeAll,
    $localize`:@@shared.staticMessage.errFieldCannotBeAll:- (---) name cannot be 'All'.`,
  ],
  [
    StaticMessage.FieldNameContainsQuotationMark,
    $localize`:@@shared.staticMessage.errFieldNameContainsQuotationMark:- The following character: <b>"</b> is not allowed to be used in the (---) field.`,
  ],
  [
    StaticMessage.FieldNameIsReservedExpression,
    $localize`:@@shared.staticMessage.errFieldNameIsReservedExpression:- <b>(---)</b> is a reserved expression and is not allowed to be used in the (---) field`,
  ],
  [
    StaticMessage.FieldNameContainsInvalidCharacters,
    $localize`:@@shared.staticMessage.errFieldNameContainsInvalidCharacters:- The following characters: <b>\\ / : * ? " < > |</b> are not allowed to be used in the (---) field.`,
  ],
  [
    StaticMessage.FieldNameMustContainLetter,
    $localize`:@@shared.staticMessage.errFieldNameMustContainLetter:- The (---) field must contain at least one letter.`,
  ],
  [
    StaticMessage.EntitiesCountExceeded,
    $localize`:@@shared.staticMessage.errEntitiesCountExceeded:- Maximum number of (---) exceeded.`,
  ],
  [
    StaticMessage.InlineCustomDataExceeded,
    $localize`:@@shared.staticMessage.errInlineCustomDataExceeded:Choose only 4 custom data columns to display for: <br>`,
  ],
  [
    StaticMessage.TabNameDuplicate,
    $localize`:@@shared.staticMessage.errTabNameDuplicate:already defined on <b>(---)</b>. Please define each tab/subtab name (<b>(---)</b>) only once.`,
  ],

  // formula validation errors
  [
    StaticMessage.MissingOperator,
    $localize`:@@shared.staticMessage.errMissingOperator:Please include a mathematical operator (e.g. '+', '-', '/', or '*') (---).`,
  ],
  [
    StaticMessage.InvalidOperatorAfterOpeningParenthesis,
    $localize`:@@shared.staticMessage.errInvalidOperatorAfterOpeningParenthesis:Please ensure an opening parenthesis is not directly followed by an invalid operator. Only '-' is allowed for negative values.`,
  ],
  [
    StaticMessage.InvalidFormulaValues,
    $localize`:@@shared.staticMessage.errInvalidFormulaValues:Please ensure that all entered values are valid.`,
  ],
  [
    StaticMessage.InvalidParanthesesPlacement,
    $localize`:@@shared.staticMessage.errInvalidParanthesesPlacement:Please ensure that all parentheses are properly placed.`,
  ],
  [
    StaticMessage.CircularReferencesFound,
    $localize`:@@shared.staticMessage.errCircularReferencesFound:Please ensure that the formula does not indirectly refer back to itself (e.g. A -> B -> C -> A).`,
  ],
  [
    StaticMessage.FormulaRequiresMultipleVariables,
    $localize`:@@shared.staticMessage.errFormulaRequiresMultipleVariables:Please ensure the formula contains more than one operand (variable or constant).`,
  ],
  [
    StaticMessage.FormulaRequiresAtLeastOneOperator,
    $localize`:@@shared.staticMessage.errFormulaRequiresAtLeastOneOperator:Please ensure the formula contains at least one operator (e.g. '+', '-', '/', or '*').`,
  ],
  [
    StaticMessage.ConsecutiveOperators,
    $localize`:@@shared.staticMessage.errConsecutiveOperators:Please ensure the formula does not contain consecutive operators (e.g. '+ +', '* /').`,
  ],
  [
    StaticMessage.InvalidStartingOperator,
    $localize`:@@shared.staticMessage.errInvalidStartingOperator:Please ensure the formula does not start with an invalid operator. Only '-' is allowed at the start for negative values.`,
  ],
  [
    StaticMessage.TrailingOperator,
    $localize`:@@shared.staticMessage.errTrailingOperator:Please ensure the formula does not end with an operator (e.g. '+', '-', '/', '*').`,
  ],
  [
    StaticMessage.DivisionByZero,
    $localize`:@@shared.staticMessage.errDivisionByZero:Division by zero is not allowed.`,
  ],

  // error messages
  [
    StaticMessage.SaveErrorTryAgain,
    $localize`:@@shared.staticMessage.txtSaveErrorTryAgain:An error has occurred while saving your data, please try again.`,
  ],
  [
    StaticMessage.SyncErrorTryAgain,
    $localize`:@@shared.staticMessage.txtSyncErrorTryAgain:An error has occurred while synchronizing (---), please try again.`,
  ],
  [
    StaticMessage.PartialSyncError,
    $localize`:@@shared.staticMessage.txtPartialSyncError:Cannot synchronize all (---) data as some mandatory fields are not provided or due to the project setup changes. Your page will be reloaded as an admin user updated the project setup.`,
  ],
  [
    StaticMessage.LockedDprError,
    $localize`:@@shared.staticMessage.txtLockedDprError:Cannot synchronize (---) as the current revision of the DPR is locked on the server.`,
  ],

  // tooltips
  [
    StaticMessage.CannotDisableItemUsedInSubprojectsTooltip,
    $localize`:@@shared.staticMessage.txtCannotDisableItemUsedInSubprojects:Can't disable (---) that is used in subprojects`,
  ],
  [
    StaticMessage.CannotDeleteRowTooltip,
    $localize`:@@shared.staticMessage.attrCannotDeleteRow:Can't be deleted due to assigned data registration`,
  ],
  [
    StaticMessage.CannotActivateFormulaColumnTooltip,
    $localize`:@@shared.staticMessage.attrCannotActivateFormulaColumn:Can't be activated while the columns used in its formula are inactive`,
  ],
  [
    StaticMessage.CannotActivateCarryOverColumnTooltip,
    $localize`:@@shared.staticMessage.attrCannotActivateCarryOverColumn:Can't be activated while the column it carries over from is inactive`,
  ],
  [
    StaticMessage.DeleteRowTooltip,
    $localize`:@@shared.staticMessage.attrDeleteRow:Delete row`,
  ],
  [
    StaticMessage.ViewModeTooltip,
    $localize`:@@shared.staticMessage.attrViewModeRow:View mode`,
  ],
  [
    StaticMessage.CopyFromPreviousDprTooltip,
    $localize`:@@shared.staticMessage.attrCopyFromPreviousDpr:Copy from previous DPR`,
  ],
  [
    StaticMessage.UnavailableInOfflineTooltip,
    $localize`:@@shared.staticMessage.attrUnavailableInOffline:Unavailable in offline mode`,
  ],
  [
    StaticMessage.CanNotCopyFirstDprTooltip,
    $localize`:@@shared.staticMessage.attrCanNotCopyFirstDpr:Cannot copy into first DPR`,
  ],
  [
    StaticMessage.NoActiveSubtabsTooltip,
    $localize`:@@shared.staticMessage.attrNoActiveSubtabs:Cannot copy when there is no active subtab`,
  ],
  [
    StaticMessage.CopyFromPrevDprNotEnabledTooltip,
    $localize`:@@shared.staticMessage.attrCopyFromPrevDprNotEnabled:Copy from previous DPR not enabled in project setup`,
  ],
  [
    StaticMessage.CanNotCopyIfDataTooltip,
    $localize`:@@shared.staticMessage.attrCanNotCopyIfData:Cannot copy as the DPR contains data`,
  ],
  [
    StaticMessage.CanNotCopyIfLockedDpr,
    $localize`:@@shared.staticMessage.attrCanNotCopyIfLockedDpr:Cannot copy as the DPR is locked`,
  ],
  [
    StaticMessage.NoEditRightsTooltip,
    $localize`:@@shared.staticMessage.attrNoEditRights:Edit not allowed`,
  ],
  [
    StaticMessage.CanNotCopyWhileDataInSectionTooltip,
    $localize`:@@shared.staticMessage.attrCanNotCopyWhileDataInSection:This button is deactivated until all data in this section has been deleted`,
  ],
  [
    StaticMessage.CanNotDownloadDataNoDprsTooltip,
    $localize`:@@shared.staticMessage.attrCanNotDownloadDataNoDprs:No DPRs available`,
  ],
  [
    StaticMessage.ProjectIsReleasedTooltip,
    $localize`:@@shared.staticMessage.attrProjectIsReleased:Project is released`,
  ],
  [
    StaticMessage.CanNotDownloadDataNoDownloadRightsTooltip,
    $localize`:@@shared.staticMessage.attrCanNotDownloadDataNoDownloadRights:Download not allowed`,
  ],
  [
    StaticMessage.ProjectBasicPersonnelVersionTooltip,
    $localize`:@@shared.staticMessage.attrProjectBasicPersonnelVersion:Value can not be changed for legacy project`,
  ],
  [
    StaticMessage.CanNotDeactivateMandatoryTabsTooltip,
    $localize`:@@shared.staticMessage.attrCanNotDeactivateMandatoryTabs:Can not deactivate mandatory tabs`,
  ],
  [
    StaticMessage.CanNotDeactivateTooltip,
    $localize`:@@shared.staticMessage.attrCanNotDeactivate:Can't deactivate: (---).`,
  ],
  [
    StaticMessage.AddShortcutTooltip,
    $localize`:@@shared.staticMessage.addShortcutTooltip:Add Entry (Shift and +)`,
  ],

  // grid columns
  [
    StaticMessage.PriorityColumn,
    $localize`:@@shared.staticMessage.colPriority:#`,
  ],
  [
    StaticMessage.CommentColumn,
    $localize`:@@shared.staticMessage.colComment:Comment`,
  ],
  [
    StaticMessage.ActiveColumn,
    $localize`:@@shared.staticMessage.colActive:Active`,
  ],
  [
    StaticMessage.MandatoryColumn,
    $localize`:@@shared.staticMessage.colMandatory:Mandatory`,
  ],
  [
    StaticMessage.PreviousColumn,
    $localize`:@@shared.staticMessage.colPrevious:Previous`,
  ],
  [
    StaticMessage.TodayColumn,
    $localize`:@@shared.staticMessage.colToday:Today`,
  ],
  [
    StaticMessage.TotalColumn,
    $localize`:@@shared.staticMessage.colTotal:Total`,
  ],
  [
    StaticMessage.ActivityGroupColumn,
    $localize`:@@shared.staticMessage.colActivityGroup:Activity Group`,
  ],
  [
    StaticMessage.ActivityColumn,
    $localize`:@@shared.staticMessage.colActivity:Activity`,
  ],
  [
    StaticMessage.LocationColumn,
    $localize`:@@shared.staticMessage.colLocation:Location`,
  ],
  [
    StaticMessage.StartColumn,
    $localize`:@@shared.staticMessage.colStart:Start`,
  ],
  [
    StaticMessage.FinishColumn,
    $localize`:@@shared.staticMessage.colFinish:Finish`,
  ],
  [
    StaticMessage.DurationColumn,
    $localize`:@@shared.staticMessage.colDuration:Duration`,
  ],
  [
    StaticMessage.DataTypeColumn,
    $localize`:@@shared.staticMessage.colDataType:Data Type`,
  ],

  // labels
  [
    StaticMessage.RegistrationsLabel,
    $localize`:@@shared.staticMessage.lblRegistrations:Registrations`,
  ],
  [
    StaticMessage.AttachmentsLabel,
    $localize`:@@shared.staticMessage.lblAttachments:Attachments`,
  ],
  [
    StaticMessage.CommentLabel,
    $localize`:@@shared.staticMessage.lblComment:Comment`,
  ],
  [
    StaticMessage.CommentPluralLabel,
    $localize`:@@shared.staticMessage.lblComments:Comments`,
  ],
  [
    StaticMessage.CustomDataLabel,
    $localize`:@@shared.staticMessage.lblCustomData:Custom Data`,
  ],
  [StaticMessage.EntryLabel, $localize`:@@shared.staticMessage.entry:entry`],
  [
    StaticMessage.EntriesLabel,
    $localize`:@@shared.staticMessage.entries:entries`,
  ],
  [StaticMessage.AllLabel, $localize`:@@shared.staticMessage.all:All`],
  [StaticMessage.SearchLabel, $localize`:@@shared.staticMessage.search:Search`],
  [
    StaticMessage.NoSearchResultsLabel,
    $localize`:@@shared.staticMessage.noSearchResults:No search results`,
  ],

  // entity labels
  [
    StaticMessage.DefaultEntityNameLabel,
    $localize`:@@shared.staticMessage.row:row`,
  ],
  [
    StaticMessage.DefaultEntityPluralNameLabel,
    $localize`:@@shared.staticMessage.rows:rows`,
  ],
  [
    StaticMessage.DefaultEntityAddNameLabel,
    $localize`:@@shared.staticMessage.addEntry:Add Entry`,
  ],
  [
    StaticMessage.UnassignedLabel,
    $localize`:@@shared.staticMessage.unassigned:Unassigned`,
  ],

  // select options
  [
    StaticMessage.NoneOptionName,
    $localize`:@@shared.staticMessage.noneOptionName:None`,
  ],
  [
    StaticMessage.AllActivityStreamsOption,
    $localize`:@@shared.staticMessage.allActivityStreamsOption:All Activity Streams`,
  ],

  // warning data types

  [
    StaticMessage.WarningLocationCategory,
    $localize`:@@shared.staticMessage.warningLocationCategory:Location Category`,
  ],
  [
    StaticMessage.WarningActivityGroup,
    $localize`:@@shared.staticMessage.warningActivityGroup:Activity Group`,
  ],
  [
    StaticMessage.WarningCustomData,
    $localize`:@@shared.staticMessage.warningCustomData:Custom Data`,
  ],
  [
    StaticMessage.WarningActivityStream,
    $localize`:@@shared.staticMessage.warningActivityStream:Activity Stream`,
  ],
  [
    StaticMessage.WarningActivity,
    $localize`:@@shared.staticMessage.warningActivity:Activity`,
  ],
  [
    StaticMessage.WarningLocation,
    $localize`:@@shared.staticMessage.warningLocation:Location`,
  ],
  [
    StaticMessage.WarningUndeclaredActivity,
    $localize`:@@shared.staticMessage.warningUndeclaredActivity:Please add the (---) to the activities sheet in your excel file to proceed with assigning custom data to it.`,
  ],

  // limits
  [
    StaticMessage.MaxLimitMessage,
    $localize`:@@shared.staticMessage.maxLimitMessage:max (---) (---)`,
  ],
]);
